const eventTypeToColumnMapper = {
  access_track: 'Page Views',
  play_track: 'Streams',
  download_track: 'Downloads/Saves',
}
type SourceObject = {
  source_id: number
  source_type: string
  source_name: string
}
type Track = {
  name: string
  id: number
}
export type AggregatedStats = {
  pageViews: number
  downloads: number
  streams: number
  uniqueListeners: number
}
type ListenerEvent = {
  source: SourceObject
  event_type: string
  timestamp: Date
}
type ListenerData = {
  events: ListenerEvent[]
  listener_name: string
  listener_id: number
}
type SourceData = {
  Source: SourceObject
  uniqueListeners: number
  Streams: number
}
type TransformedListenerData = {
  Name: string
  id: number
  'Page Views': number
  'Downloads/Saves': number
  Streams: number
  'Last Activity': Date
}

const _getPlaylistName = sourceName => {
  // Playlist names get returned from the API wrapped in `<>` so we slice to get the name
  if (sourceName && sourceName.length > 2) {
    return `${sourceName.slice(1, sourceName.length - 1)}`
  } else {
    return `Playlist`
  }
}

const getSourceName = (source, track: Track) => {
  if (source.source_type === 'playlist') {
    let sourceName = source.source_name
    return _getPlaylistName(sourceName)
  }

  if (source.source_type === 'track') {
    return `Track: ${track.name}`
  }

  if (source.source_type === 'embed') {
    return `Embed Player: ${track.name}`
  }

  if (source.source_type === 'catalog') {
    return `Catalog: ${source.source_name}`
  }

  return 'Other Sources'
}

const getSourceLink = (source, track) => {
  if (!source) {
    return null
  }

  if (source.source_type === 'playlist') {
    return `/explorer/playlists/${source.source_id}`
  }

  if (source.source_type === 'track') {
    return `/explorer/tracks/${source.source_id}`
  }

  if (source.source_type === 'embed') {
    return `/explorer/tracks/${track.id}`
  }

  if (source.source_type === 'catalog') {
    return `/catalogs/${source.source_id}`
  }

  return null
}

export const makeSourceData: (
  listenerData: ListenerData[],
  track: Track,
) => Record<string, any>[] = (listenerData, track) => {
  let sourceData = {}
  listenerData.forEach(ld => {
    ld.events.forEach(eventData => {
      let sourceName = 'Other Sources'

      if (eventData.source) {
        sourceName = getSourceName(eventData.source, track)
      }

      if (!(sourceName in sourceData)) {
        sourceData[sourceName] = {
          Source: {
            sourceLink: getSourceLink(eventData.source, track),
            sourceText: sourceName,
          },
          uniqueListeners: [],
          'Page Views': 0,
          'Downloads/Saves': 0,
          Streams: 0,
        }
      }

      if (!sourceData[sourceName]['uniqueListeners'].includes(ld.listener_id)) {
        sourceData[sourceName]['uniqueListeners'].push(ld.listener_id)
      }

      const eventKey = eventTypeToColumnMapper[eventData.event_type]
      sourceData[sourceName][eventKey] += 1
    })
  })
  return Object.values(sourceData)
}
export const transformListenerData: (
  listenerData: ListenerData[],
  isTrackSharingEnabled: boolean,
) => TransformedListenerData[] = (listenerData, isTrackSharingEnabled) => {
  if (!isTrackSharingEnabled) {
    const allEvents = listenerData.flatMap(item => item.events)
    return [
      {
        Name: 'Anonymous',
        id: -1,
        'Page Views': allEvents.filter(
          event => event.event_type === 'access_track',
        ).length,
        'Downloads/Saves': allEvents.filter(
          event => event.event_type === 'download_track',
        ).length,
        Streams: allEvents.filter(event => event.event_type === 'play_track')
          .length,
        'Last Activity': allEvents.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        )[0].timestamp,
      },
    ]
  }

  return listenerData.map(item => ({
    Name: item.listener_name,
    id: item.listener_id,
    'Page Views': item.events.filter(
      event => event.event_type === 'access_track',
    ).length,
    'Downloads/Saves': item.events.filter(
      event => event.event_type === 'download_track',
    ).length,
    Streams: item.events.filter(event => event.event_type === 'play_track')
      .length,
    'Last Activity': item.events.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )[0].timestamp,
  }))
}
export const getAggregates: (
  transformedListenerData: TransformedListenerData[],
) => AggregatedStats = transformedListenerData => {
  let aggregates = {
    uniqueListeners: 0,
    pageViews: 0,
    downloads: 0,
    streams: 0,
  }

  for (let i = 0; i < transformedListenerData.length; i++) {
    aggregates['pageViews'] += transformedListenerData[i]['Page Views']
    aggregates['downloads'] += transformedListenerData[i]['Downloads/Saves']
    aggregates['streams'] += transformedListenerData[i]['Streams']
    aggregates['uniqueListeners'] += 1
  }

  return aggregates
}
